import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button, List, ListItem, ListItemText, TextField } from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { useParams } from 'react-router';
import useFetchDelivery from 'api/hooks/useFetchDelivery';
import { useUpdateTrayInspection } from 'api/hooks/useUpdateTrayInspection';
import { TrayInspectionInfo, TrayInspectionResults } from 'api/models/Trays';
import Theme from 'App.theme';
import Dialog from 'components/Dialog';
import { useLanguage } from 'context/AppContext';

interface MissingDevicesModalProps {
  open: boolean;
  close: VoidFunction;
  deliveryId: string;
  onChange?: (key: keyof TrayInspectionResults, val: boolean) => void;
  inspection: TrayInspectionInfo;
  trayId?: string;
  devicesMissingModalOpen?: boolean;
  onConfirmMissingDevices?: (missingDevices: string[]) => void;
}

const MissingDevicesModal: FC<MissingDevicesModalProps> = ({
  open,
  close,
  deliveryId,
  inspection,
  trayId: passedTrayId,
  onConfirmMissingDevices,
}) => {
  const lang = useLanguage()[0].CheckInTrayPage;
  const [missingDevices, setMissingDevices] = useState<string[]>([]);
  const [inputText, setInputText] = useState('');
  const { setId, trayId = passedTrayId } = useParams();
  const { tray } = useFetchDelivery(deliveryId, setId, trayId);
  const addDeviceButtonDisabled = !inputText.trim().length;

  // saved inspection state to the api and reloads the delivery
  const { mutateAsync: updateTrayInspection } = useUpdateTrayInspection();

  const navigateDeliveryPortalClick = () => {
    close();
  };

  // Callback function to receive the result from the child component
  const handleConfirmMissingDevices = useCallback(async () => {
    if (!tray) {
      throw new Error('No tray!');
    }

    if (onConfirmMissingDevices) {
      onConfirmMissingDevices(missingDevices);
      return;
    }

    const ins = { ...inspection };
    if (ins['missingParts'] == missingDevices) return;
    ins['missingParts'] = missingDevices;
    inspection['missingParts'] = missingDevices;

    await updateTrayInspection({
      trayId: tray.trayInstanceId,
      inspectionInfo: ins,
    });

    close();
  }, [tray, onConfirmMissingDevices, inspection, missingDevices, updateTrayInspection, close]);

  const addMissingDevice = () => {
    setMissingDevices([...missingDevices, inputText]);
    setInputText('');
  };

  return (
    <Dialog
      open={open}
      title={lang.listMissingDevices}
      onEnterKey="confirm"
      cancelLabel={lang.cancel}
      confirmLabel={lang.addMissingDevice}
      onConfirm={handleConfirmMissingDevices}
      onCancel={navigateDeliveryPortalClick}
    >
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
        <TextField label="Device code" value={inputText} onChange={e => setInputText(e.target.value)} />
        <Button disabled={addDeviceButtonDisabled} onClick={addMissingDevice}>
          <AddCircleIcon
            fontSize="large"
            sx={{ color: Theme.colours.active, opacity: addDeviceButtonDisabled ? 0.3 : 1 }}
          />
        </Button>
      </div>
      <List style={{ height: '100px', overflow: 'auto' }}>
        {missingDevices.map((item, index) => (
          <ListItem key={index}>
            <ListItemText primary={index + 1 + '. ' + item} />
          </ListItem>
        ))}
      </List>
    </Dialog>
  );
};

export default MissingDevicesModal;
